import React, { useMemo } from 'react'
import { PieChart, PieChartProps } from 'react-minimal-pie-chart'

import { useAccount } from '../../../../../common/useAccount'

import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { useAppSelector } from '../../../../../store/hooks'

import PrizeBadge from '../../../../../components/ScopedBlock/PrizeBadge.svg?url'
import { useZocksQuota } from '../../../../../components/ScopedBlock/hooks'

import { parseMetadata } from '../utils'

import { roundDistributionTo100 } from './helper'

import './MeetingOverallSentiment.scss'

export type OverallSentimentChartOptions = {
  data: PieChartProps['data']
  overall: number
  distribution: {
    [key in SentimentType]: number
  }
}

type SentimentType = 'positive' | 'neutral' | 'negative'
export type SentimentAnalysisMetadata = {
  sentimentAnalysis:
    | {
        overall: number
        distribution: {
          [key in SentimentType]: number
        }
      }
    | { [key: string]: any }
}

const _SENTIMENT_COLOR_MAP: {
  [key in SentimentType]: string
} = {
  positive: '#5227CC',
  neutral: '#FF7E21',
  negative: '#FF0051',
}

const _SENTIMENT_COLOR_MAP_KEYS = Object.keys(_SENTIMENT_COLOR_MAP) as SentimentType[]

const _SENTIMENT_LABELS: { [key in SentimentType]: string } = {
  positive: 'Positive',
  neutral: 'Neutral',
  negative: 'Negative',
}

const _SENTIMENT_ICON_MAP: { [key in SentimentType]: string } = {
  positive: 'bi-emoji-smile-fill',
  neutral: 'bi-emoji-neutral-fill',
  negative: 'bi-emoji-frown-fill',
}

export const MeetingOverallSentiment = () => {
  const { hasQuota } = useZocksQuota()
  const { account } = useAccount({ refetchOnMountOrArgChange: false })

  const sentimentAnalysisEnabled = hasQuota('ai.sentimentAnalysis.value')

  if (!account?.effectiveConfig.sentimentAnalysis) {
    return null
  }

  if (!sentimentAnalysisEnabled) {
    return (
      <div className='disabled-meeting-sentiment-container p-4 border rounded-2 position-relative'>
        <div className='disabled-meeting-sentiment-inner-container d-flex flex-column justify-content-center gap-1'>
          <span className='paragraph-small text-nowrap'>Upgrade to access Meeting Sentiment</span>
          <a
            className='zocks-text-primary paragraph-small text-decoration-underline'
            href='https://help.zocks.io/en/articles/11123958-how-to-use-sentiment-analysis-in-zocks'
            target='_blank'
            rel='noopener noreferrer'
          >
            Learn more
          </a>
        </div>
        <img className='position-absolute' alt='Upgrade' src={PrizeBadge} />
      </div>
    )
  }

  return <MeetingOverallSentimentContent />
}

const MeetingOverallSentimentContent = () => {
  const roomSession = useAppSelector(selectRoomSession)

  const sentimentAnalysisChartData = useMemo((): OverallSentimentChartOptions | null => {
    if (!roomSession) {
      return null
    }

    const metadata = parseMetadata(roomSession.metadata) as SentimentAnalysisMetadata
    const distribution = metadata?.sentimentAnalysis?.distribution ?? null
    const overall = metadata?.sentimentAnalysis?.overall ?? null

    if (!overall || !distribution) {
      return null
    }

    const rounded = roundDistributionTo100(distribution)

    return {
      overall,
      distribution: rounded,
      data: _SENTIMENT_COLOR_MAP_KEYS.map((key) => ({
        title: _SENTIMENT_LABELS[key],
        value: rounded[key],
        color: _SENTIMENT_COLOR_MAP[key],
      })),
    }
  }, [roomSession])

  if (!sentimentAnalysisChartData) {
    return null
  }

  return (
    <div className='meeting-sentiment-container d-flex align-items-center zocks-gap-1.25 p-4 border rounded-2 zocks-border-color-gray-300'>
      <div className='sentiment-chart-container'>
        <PieChart data={sentimentAnalysisChartData.data} lineWidth={16} style={{ width: '3.5rem', height: '3.5rem' }} startAngle={320}></PieChart>
        <div className='sentiment-value-container d-flex align-items-center justify-content-center'>
          <h6>{sentimentAnalysisChartData.overall}</h6>
        </div>
      </div>
      <div className='d-flex flex-column gap-1'>
        <span className='lead-small zocks-text-gray-900'>Sentiment Analysis</span>
        <div className='d-flex align-items-center gap-2'>
          {_SENTIMENT_COLOR_MAP_KEYS.map((key) => (
            <div key={key} className='d-flex align-items gap-1' style={{ color: _SENTIMENT_COLOR_MAP[key] }}>
              <i className={`bi ${_SENTIMENT_ICON_MAP[key]}`} />
              <span className='bold'>{sentimentAnalysisChartData.distribution[key]}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
